/* eslint-disable max-len, max-lines */
import {type PlayerColor} from './PlayerState.ts'
import {type CardDistributionType, type CardEnum} from './CardState.ts'
import {type TileHexType} from './map-state/TileHexState.ts'
import {type TileEdgeType} from './map-state/TileEdgeState.ts'
import {type CityImprovementTypes} from './mechanic-states/MechanicCityImprovements.ts'

export interface GameLogData {
    text: GameLog
    from?: PlayerColor
    specificRecipients?: PlayerColor[] // default: leave undefined to send to all players
    toSpectators?: false // default: undefined will send to spectators
}

export interface GameLogKnightInformation {
    ownerColor: PlayerColor
    level: number
    isActivated: boolean
}

export interface GameLogTileInformation {
    tileType: TileHexType
    diceNumber: number
    resourceType: CardEnum | undefined
}

export enum GameLogPieceEnum {
    Road = 0,
    Ship = 1,
    Settlement = 2,
    City = 3,
    CityWall = 4,
    Robber = 5,
    Pirate = 6,
}

export enum GameLogAchievementEnum {
    LongestRoad = 0,
    LargestArmy = 1,
}

export enum GameLogType {
    PlayerReconnecting = 0,
    BoughtDevelopmentCard = 1,
    WelcomeMessage = 2,
    StartedSpectating = 3,
    PlayerPlacedPiece = 4,
    BuiltPiece = 5,
    PlayerMovedPiece = 6,
    UpgradedKnight = 7,
    ActivatedKnight = 8,
    RemovedKnight = 9,
    RolledDice = 10,
    MovedRobber = 11,
    PlacedMetropolis = 12,
    ReceivedCard = 13,
    StolenResourceCardThief = 14,
    StolenResourceCardVictim = 15,
    StolenResourceCardClosed = 16,
    StolenDevelopmentCardThief = 17,
    StolenDevelopmentCardVictim = 18,
    StolenDevelopmentCardClosed = 19,
    PlayerPlayedDevelopmentCard = 20,
    YearOfPlentyTookFromBank = 21,
    LeftTheGame = 22,
    WasInactiveAndKicked = 23,
    Disconnected = 24,
    StoppedSpectating = 25,
    KarmaPenaltyRemoved = 26,
    KarmaSystemDisabledDueToPlayersLeaving = 27,
    KarmaSystemInactiveCannotVoteToDisable = 28,
    KarmaSystemIsAlreadyDisabled = 29,
    YouCantStartVoteBecauseYouAreTheOnlyPlayerLeft = 30,
    YouCantStartVoteDueToYourLeaverStatus = 31,
    YouCanOnlyStartAVoteToDisableKarmaOnce = 32,
    KarmaSystemVoteToDisableSucceeded = 33,
    KarmaSystemVoteToDisableDeclined = 34,
    ThereIsAlreadyAnActiveVote = 35,
    PlayerStartedAVoteToDisableKarma = 36,
    GameEndMessage = 37,
    GameEndChatDisabled = 38,
    PlayerCouldNotStealFrom = 39,
    GameCrashedZero = 40,
    GameCrashedOne = 41,
    GameCrashedTwo = 42,
    GameCrashedThree = 43,
    Separator = 44,
    PlayerWonTheGame = 45,
    GameEndedDueToServerRestart = 46,
    ResourceDistribution = 47,
    NotEnoughCardsInBankToDistribute = 48,
    TileBlockedByRobber = 49,
    PlayersSelectingResourcesFromAqueduct = 50,
    BotSelectingAqueductResourcesFor = 51,
    NotEnoughProgressCardsInBankToDistribute = 52,
    PlayersDiscardingProgressCards = 53,
    PlayerDiscarded = 55,
    PlayersSelectingResourceFromGoldTile = 56,
    FriendlyPirateActive = 57,
    BotIsSelectingWhereToPlacePieceFor = 58,
    RobberDisabledUntilFirstBarbarianAttack = 59,
    FriendlyRobberActive = 60,
    LostMetropolis = 61,
    PlayerUpgradedImprovementTo = 62,
    BotIsSelectingGoldTileResourceFor = 63,
    BotIsSelectingCardsToDiscardFor = 64,
    PlayerSettledOnForeignLand = 65,
    PlayerReceivedAchievement = 66,
    PlayerLostAchievement = 67,
    PlayerPassedAchievementTo = 68,
    PlayerMovedMerchantTo = 69,
    PlayerLostPossessionOfMerchant = 70,
    BotIsSelectingWhereToPlaceMerchantFor = 71,
    BotIsPlacing = 72,
    BotIsSelectingWhoToRobFor = 73,
    NoPlayerToStealFrom = 74,
    SkippingSpecialBuildPhase = 75,
    PlayerEnteredSpecialBuildPhase = 76,
    BotIsSelectingProgressCardFor = 77,
    PlayerIsSpyingPlayer = 78,
    PlayersDefeatedBarbarians = 79,
    PlayerAwardedDefenderOfColonist = 80,
    PlayersSelectingProgressCard = 81,
    BotIsSelectingPieceToDestroyFor = 82,
    BarbariansAttackedButNoCityToPillage = 83,
    PlayersDefeatedByBarbarians = 84,
    PlayersPieceDestroyedByBarbarians = 85,
    PlayerStoleUsingMonopoly = 86,
    BotIsPlacingPieceFor = 87,
    BotIsSelectingWhereToPlaceKnightFor = 88,
    PlayerUsedPieceToMovePiece = 89,
    PlayerDisplacedPiece = 90,
    BotSelectingWhereToMoveKnightFor = 91,
    PlayerRemovedPlayersKnight = 92,
    BotSelectingKnightToRemoveFor = 93,
    BotSelectingKnightToUpgradeFor = 94,
    PlayerReceivedVPProgressCard = 95,
    PlayerSelectedPlayer = 96,
    PlayerRepositioningEdgePiece = 97,
    PlayerRemovedEdgePiece = 98,
    BotIsSelectingEdgePieceToRemoveFor = 99,
    BotSelectingResourcesFor = 100,
    BotSelectingDiceFor = 101,
    PlayerSwappedTileNumbers = 102,
    ExchangedCardsThief = 103,
    ExchangedCardsVictim = 104,
    ExchangedCardsClosed = 105,
    PlayerIsStealingFrom = 106,
    PlayerSelectedCard = 107,
    BotLogicNotSupportedForCitiesAndKnights = 108,
    PlayerUnlockedAqueduct = 109,
    PlayerUnlockedTradingHouse = 110,
    PlayerUnlockedFortress = 111,
    PlayerResigned = 112,
    PlayerStartedEmbargo = 113,
    PlayerEndedEmbargo = 114,
    PlayerTradedWithPlayer = 115,
    PlayerTradedWithBank = 116,
    PlayerWantsToCounterOfferWith = 117,
    PlayerWantsToTradeWith = 118,
    PlayerCreatedRematch = 119,
    PlayersJoinedRematch = 120,
    YouCanOnlyStartAVoteToPauseXTimes = 121,
    GamePaused = 122,
    GameResumed = 123,
    PauseSystemPlayerStartedVotePause = 124,
    PauseSystemPlayerStartedVoteResume = 125,
    PauseSystemVoteSucceededPause = 126,
    PauseSystemVoteSucceededResume = 127,
    PauseSystemVoteDeclinedPause = 128,
    PauseSystemVoteDeclinedResume = 129,
    YouAreLastPlayerYouWillWinIfOpponentDoesNotReconnect = 130,
    VoteHasBeenCanceledBecauseGameEnded = 131,
    PlayerPlacedKnight = 132,
    PlayerMovedKnight = 133,
    ChatCommandGetAllResources = 134,
    ChatCommandGetCard = 135,
    ChatCommandRollAll = 136,
    ChatCommandRollDice = 137,
    ChatCommandRollNextDice = 138,
    CantStealPlayersDontHaveCards = 139,
    PermanentlyKickedForGoingAFKTooManyTime = 140,
    DiceRolledAutomatically = 141,
}

export type GameLog =
{
    type: GameLogType.PlayerReconnecting
    playerColor: PlayerColor
} | {
    type: GameLogType.BoughtDevelopmentCard
    playerColor: PlayerColor
} | {
    type: GameLogType.WelcomeMessage
    isDiscord: boolean
} | {
    type: GameLogType.StartedSpectating
    username: string
} | {
    type: GameLogType.PlayerPlacedPiece
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
} | {
    type: GameLogType.BuiltPiece
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
    isVp: boolean
} | {
    type: GameLogType.PlayerMovedPiece
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
} | {
    type: GameLogType.UpgradedKnight
    playerColor: PlayerColor
    oldKnightInfo: GameLogKnightInformation
    newKnightInfo: GameLogKnightInformation
} | {
    type: GameLogType.ActivatedKnight
    playerColor: PlayerColor
    knightInfo: GameLogKnightInformation
} | {
    type: GameLogType.RemovedKnight
    playerColor: PlayerColor
    knightInfo: GameLogKnightInformation
} | {
    type: GameLogType.RolledDice
    playerColor: PlayerColor
    firstDice: number
    secondDice: number
    eventDice: number | undefined
} | {
    type: GameLogType.DiceRolledAutomatically
    firstDice: number
    secondDice: number
    eventDice: number | undefined
} | {
    type: GameLogType.MovedRobber
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
    tileInfo: GameLogTileInformation
} | {
    type: GameLogType.PlacedMetropolis
    playerColor: PlayerColor
    metropolisType: CityImprovementTypes
} | {
    type: GameLogType.ReceivedCard
    playerColor: PlayerColor
    cardEnum: CardEnum
} | {
    type: GameLogType.StolenResourceCardThief
    playerColor: PlayerColor
    cardEnums: CardEnum[]
} | {
    type: GameLogType.StolenResourceCardVictim
    playerColor: PlayerColor
    cardEnums: CardEnum[]
} | {
    type: GameLogType.StolenResourceCardClosed
    playerColorThief: PlayerColor
    playerColorVictim: PlayerColor
    cardBacks: CardEnum[]
} | {
    type: GameLogType.StolenDevelopmentCardThief
    playerColor: PlayerColor
    cardEnum: CardEnum
} | {
    type: GameLogType.StolenDevelopmentCardVictim
    playerColor: PlayerColor
    cardEnum: CardEnum
} | {
    type: GameLogType.StolenDevelopmentCardClosed
    playerColorThief: PlayerColor
    playerColorVictim: PlayerColor
    cardBack: CardEnum
} | {
    type: GameLogType.PlayerPlayedDevelopmentCard
    playerColor: PlayerColor
    cardEnum: CardEnum
} | {
    type: GameLogType.YearOfPlentyTookFromBank
    playerColor: PlayerColor
    cardEnums: CardEnum[]
} | {
    type: GameLogType.LeftTheGame
    playerColor: PlayerColor
} | {
    type: GameLogType.WasInactiveAndKicked
    playerColor: PlayerColor
} | {
    type: GameLogType.Disconnected
    playerColor: PlayerColor
    is10SecondRuleDisabled: boolean
} | {
    type: GameLogType.StoppedSpectating
    username: string
} | {
    type: GameLogType.KarmaPenaltyRemoved
} | {
    type: GameLogType.KarmaSystemDisabledDueToPlayersLeaving
} | {
    type: GameLogType.KarmaSystemInactiveCannotVoteToDisable
} | {
    type: GameLogType.KarmaSystemIsAlreadyDisabled
} | {
    type: GameLogType.YouCantStartVoteBecauseYouAreTheOnlyPlayerLeft
} | {
    type: GameLogType.YouCantStartVoteDueToYourLeaverStatus
} | {
    type: GameLogType.YouCanOnlyStartAVoteToDisableKarmaOnce
} | {
    type: GameLogType.KarmaSystemVoteToDisableSucceeded
} | {
    type: GameLogType.KarmaSystemVoteToDisableDeclined
} | {
    type: GameLogType.ThereIsAlreadyAnActiveVote
} | {
    type: GameLogType.PlayerStartedAVoteToDisableKarma
    playerColor: PlayerColor
} | {
    type: GameLogType.GameEndMessage
} | {
    type: GameLogType.GameEndChatDisabled
} | {
    type: GameLogType.PlayerCouldNotStealFrom
    playerColorStealing: PlayerColor
    playerColorVictim: PlayerColor
} | {
    type: GameLogType.GameCrashedZero
} | {
    type: GameLogType.GameCrashedOne
} | {
    type: GameLogType.GameCrashedTwo
    dateString: string
} | {
    type: GameLogType.GameCrashedThree
} | {
    type: GameLogType.Separator
} | {
    type: GameLogType.PlayerWonTheGame
    playerColor: PlayerColor
} | {
    type: GameLogType.GameEndedDueToServerRestart
} | {
    type: GameLogType.ResourceDistribution
    playerColor: PlayerColor
    cardsToBroadcast: CardEnum[]
    distributionType: CardDistributionType
} | {
    type: GameLogType.NotEnoughCardsInBankToDistribute
    cardEnum: CardEnum
    count: number | undefined
    bankCount: number | undefined
} | {
    type: GameLogType.TileBlockedByRobber
    tileInfo: GameLogTileInformation
} | {
    type: GameLogType.PlayersSelectingResourcesFromAqueduct
    playerColors: PlayerColor[]
} | {
    type: GameLogType.BotSelectingAqueductResourcesFor
    playerColor: PlayerColor
} | {
    type: GameLogType.NotEnoughProgressCardsInBankToDistribute
} | {
    type: GameLogType.PlayersDiscardingProgressCards
    playerColors: PlayerColor[]
} | {
    type: GameLogType.PlayerDiscarded
    playerColor: PlayerColor
    cardEnums: CardEnum[]
    areResourceCards: boolean
} | {
    type: GameLogType.PlayersSelectingResourceFromGoldTile
    playerColors: PlayerColor[]
} | {
    type: GameLogType.FriendlyPirateActive
    all: boolean
} | {
    type: GameLogType.BotIsSelectingWhereToPlacePieceFor
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
} | {
    type: GameLogType.RobberDisabledUntilFirstBarbarianAttack
} | {
    type: GameLogType.FriendlyRobberActive
    all: boolean
} | {
    type: GameLogType.LostMetropolis
    playerColor: PlayerColor
    metropolisType: CityImprovementTypes
} | {
    type: GameLogType.PlayerUpgradedImprovementTo
    playerColor: PlayerColor
    improvementType: CityImprovementTypes
    newCityLevel: number
} | {
    type: GameLogType.BotIsSelectingGoldTileResourceFor
    playerColor: PlayerColor
} | {
    type: GameLogType.BotIsSelectingCardsToDiscardFor
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerSettledOnForeignLand
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerReceivedAchievement
    playerColor: PlayerColor
    achievementEnum: GameLogAchievementEnum
} | {
    type: GameLogType.PlayerLostAchievement
    playerColor: PlayerColor
    achievementEnum: GameLogAchievementEnum
} | {
    type: GameLogType.PlayerPassedAchievementTo
    playerColorOld: PlayerColor
    playerColorNew: PlayerColor
    achievementEnum: GameLogAchievementEnum
} | {
    type: GameLogType.PlayerMovedMerchantTo
    playerColor: PlayerColor
    tileInfo: GameLogTileInformation
    isMerchantOwnerDisplacingMerchant: boolean
} | {
    type: GameLogType.PlayerLostPossessionOfMerchant
    playerColor: PlayerColor
    merchantOwnerColor: PlayerColor
} | {
    type: GameLogType.BotIsSelectingWhereToPlaceMerchantFor
    playerColor: PlayerColor
} | {
    type: GameLogType.BotIsPlacing
    pieceEnum: GameLogPieceEnum.Road | GameLogPieceEnum.Ship
    playerColor: PlayerColor
} | {
    type: GameLogType.BotIsSelectingWhoToRobFor
    playerColor: PlayerColor
} | {
    type: GameLogType.NoPlayerToStealFrom
} | {
    type: GameLogType.SkippingSpecialBuildPhase
} | {
    type: GameLogType.PlayerEnteredSpecialBuildPhase
    playerColor: PlayerColor
} | {
    type: GameLogType.BotIsSelectingProgressCardFor
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerIsSpyingPlayer
    playerColor: PlayerColor
    playerColorVictim: PlayerColor
} | {
    type: GameLogType.PlayersDefeatedBarbarians
} | {
    type: GameLogType.PlayerAwardedDefenderOfColonist
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayersSelectingProgressCard
    playerColors: PlayerColor[]
} | {
    type: GameLogType.BotIsSelectingPieceToDestroyFor
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
} | {
    type: GameLogType.BarbariansAttackedButNoCityToPillage
} | {
    type: GameLogType.PlayersDefeatedByBarbarians
} | {
    type: GameLogType.PlayersPieceDestroyedByBarbarians
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
} | {
    type: GameLogType.PlayerStoleUsingMonopoly
    playerColor: PlayerColor
    amountStolen: number
    cardEnum: CardEnum
} | {
    type: GameLogType.BotIsPlacingPieceFor
    playerColor: PlayerColor
    pieceEnum: GameLogPieceEnum
} | {
    type: GameLogType.BotIsSelectingWhereToPlaceKnightFor
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerUsedPieceToMovePiece
    playerColor: PlayerColor
    knightInfo: GameLogKnightInformation
    isMovedPieceRobber: boolean
} | {
    type: GameLogType.PlayerDisplacedPiece
    playerColor: PlayerColor
    displacerKnightInfo: GameLogKnightInformation
    enemyKnightInfo: GameLogKnightInformation
} | {
    type: GameLogType.BotSelectingWhereToMoveKnightFor
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerRemovedPlayersKnight
    playerColor: PlayerColor
    playerColorLosingKnight: PlayerColor
} | {
    type: GameLogType.BotSelectingKnightToRemoveFor
    playerColor: PlayerColor
} | {
    type: GameLogType.BotSelectingKnightToUpgradeFor
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerReceivedVPProgressCard
    playerColor: PlayerColor
    cardEnum: CardEnum
} | {
    type: GameLogType.PlayerSelectedPlayer
    playerColor: PlayerColor
    playerColorSelected: PlayerColor
} | {
    type: GameLogType.PlayerRepositioningEdgePiece
    playerColor: PlayerColor
    edgeType: TileEdgeType
} | {
    type: GameLogType.PlayerRemovedEdgePiece
    playerColor: PlayerColor
    playerColorLosingPiece: PlayerColor
    edgeType: TileEdgeType
} | {
    type: GameLogType.BotIsSelectingEdgePieceToRemoveFor
    playerColor: PlayerColor
    edgeType: TileEdgeType
} | {
    type: GameLogType.BotSelectingResourcesFor
    playerColor: PlayerColor
} | {
    type: GameLogType.BotSelectingDiceFor
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerSwappedTileNumbers
    playerColor: PlayerColor
    firstTileInfo: GameLogTileInformation
    secondTileInfo: GameLogTileInformation
} | {
    type: GameLogType.ExchangedCardsThief
    playerColor: PlayerColor
    givenCardEnum: CardEnum
    receivedCardEnum: CardEnum
} | {
    type: GameLogType.ExchangedCardsVictim
    playerColor: PlayerColor
    givenCardEnum: CardEnum
    receivedCardEnum: CardEnum
} | {
    type: GameLogType.ExchangedCardsClosed
    playerColorThief: PlayerColor
    playerColorVictim: PlayerColor
} | {
    type: GameLogType.PlayerIsStealingFrom
    playerColor: PlayerColor
    playerColorVictim: PlayerColor
} | {
    type: GameLogType.PlayerSelectedCard
    playerColor: PlayerColor
    cardEnum: CardEnum
} | {
    type: GameLogType.BotLogicNotSupportedForCitiesAndKnights
} | {
    type: GameLogType.PlayerUnlockedAqueduct
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerUnlockedTradingHouse
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerUnlockedFortress
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerResigned
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayerStartedEmbargo
    playerColor: PlayerColor
    embargoedPlayerColor: PlayerColor
} | {
    type: GameLogType.PlayerEndedEmbargo
    playerColor: PlayerColor
    embargoedPlayerColor: PlayerColor
} | {
    type: GameLogType.PlayerTradedWithPlayer
    playerColor: PlayerColor
    acceptingPlayerColor: PlayerColor
    givenCardEnums: CardEnum[]
    receivedCardEnums: CardEnum[]
} | {
    type: GameLogType.PlayerTradedWithBank
    playerColor: PlayerColor
    givenCardEnums: CardEnum[]
    receivedCardEnums: CardEnum[]
} | {
    type: GameLogType.PlayerWantsToCounterOfferWith
    playerColorCreator: PlayerColor
    playerColorOffered: PlayerColor
    wantedCardEnums: CardEnum[]
    offeredCardEnums: CardEnum[]
} | {
    type: GameLogType.PlayerWantsToTradeWith
    playerColor: PlayerColor
    wantedCardEnums: CardEnum[]
    offeredCardEnums: CardEnum[]
} | {
    type: GameLogType.PlayerCreatedRematch
    playerColor: PlayerColor
} | {
    type: GameLogType.PlayersJoinedRematch
    count: number
    playerUsernames: string
} | {
    type: GameLogType.YouCanOnlyStartAVoteToPauseXTimes
    count: number
} | {
    type: GameLogType.GamePaused
} | {
    type: GameLogType.GameResumed
} | {
    type: GameLogType.PauseSystemPlayerStartedVotePause
    playerColor: PlayerColor
} | {
    type: GameLogType.PauseSystemPlayerStartedVoteResume
    playerColor: PlayerColor
} | {
    type: GameLogType.PauseSystemVoteSucceededPause
} | {
    type: GameLogType.PauseSystemVoteSucceededResume
} | {
    type: GameLogType.PauseSystemVoteDeclinedPause
} | {
    type: GameLogType.PauseSystemVoteDeclinedResume
} | {
    type: GameLogType.YouAreLastPlayerYouWillWinIfOpponentDoesNotReconnect
    count: number
} | {
    type: GameLogType.VoteHasBeenCanceledBecauseGameEnded
} | {
    type: GameLogType.PlayerPlacedKnight
    knightInfo: GameLogKnightInformation
} | {
    type: GameLogType.PlayerMovedKnight
    knightInfo: GameLogKnightInformation
} | {
    type: GameLogType.ChatCommandGetAllResources
} | {
    type: GameLogType.ChatCommandGetCard
    cardEnum: CardEnum
} | {
    type: GameLogType.ChatCommandRollAll
} | {
    type: GameLogType.ChatCommandRollDice
    diceNumber: number
} | {
    type: GameLogType.ChatCommandRollNextDice
    diceNumber1: number
    diceNumber2: number
} | {
    type: GameLogType.CantStealPlayersDontHaveCards
    playerCount: number
} | {
    type: GameLogType.PermanentlyKickedForGoingAFKTooManyTime
    playerColor: PlayerColor
}
